/**
 * System Notices grid — server-side list of all system banners.
 *
 * Columns are driven by the backend grid-spec descriptor
 * (system-banners/grid-spec/): it owns the column set, order, headers, widths,
 * the enabled/staff_only enum chips, and the permission-gated Edit actions
 * column (emitted server-side for engineering/accounting/superuser). FE overrides
 * supply only the timezone-aware timestamps and the sanitized-HTML message cell.
 */

import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { EntityGrid } from '@sophocles/data-grids';
import { formatDateTime } from '@sophocles/utils/formatUtils';

// odl_app resource path for the backend grid-spec descriptor + list endpoint.
const RESOURCE_PATH = 'system-banners';

export default function SystemNoticesGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();

  // FE display overrides merged on top of the spec's type defaults: the
  // timestamps need user-timezone formatting and the message is sanitized HTML.
  const fieldOverrides = useMemo(
    () => ({
      start_time: { renderCell: ({ row }) => formatDateTime(row.start_time, user) },
      end_time: { renderCell: ({ row }) => formatDateTime(row.end_time, user) },
      html: {
        renderCell: ({ row }) => (
          <div
            className="system-notice-banner"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.html ?? '') }}
          />
        ),
      },
    }),
    [user],
  );

  // Column set is fully driven by the descriptor; the BE emits the actions column
  // gated on engineering/accounting/superuser, so no client-side append is needed.
  const actionHandlers = useMemo(
    () => ({
      edit: row => navigate(`/staff/systemnotices/${row.id}/`),
    }),
    [navigate],
  );

  return (
    <EntityGrid
      basePath={RESOURCE_PATH}
      fieldOverrides={fieldOverrides}
      actionHandlers={actionHandlers}
      options={{
        searchPlaceholder: t('systemNotices.search', 'Search notices'),
        persistKey: 'systemBannersGrid',
        infiniteScroll: true,
        defaultSort: [{ field: 'start_time', sort: 'desc' }],
      }}
    />
  );
}
